import { Button } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import clsx from 'clsx';
import React, { useEffect } from 'react';
import { FPMMessageType } from '../../common/enums';
import { ChevronDownOutline, ChevronUpOutline } from '../../icons/icons';
import { MessageType } from '../../services/actor-messages';
import { IntegrationDebugEntry } from '../../webscoket/debug/integration-message-debugger';
import { useAppDispatch, useAppSelector } from '../hooks/store-hooks';
import { useFlightWorker } from '../hooks/use-flight-worker';
import { setSetting } from '../state/slices/settings-slice';

export function IntegrationMessageDebugger(props: { onClose?: () => void }) {
    const enabled = useAppSelector((x) => x.settings.enableIntegrationMessageDebugger);
    const [messages, setMessages] = React.useState([] as IntegrationDebugEntry[]);
    const worker = useFlightWorker();
    const dispatch = useAppDispatch();

    useEffect(() => {
        worker.actor.sendAsync({ type: MessageType.debugIntegrationMessagesGet }).then((messages) => {
            setMessages(messages.toReversed());
        });
    }, []);

    useEffect(() => {
        worker.actor.registerMessageHandler(MessageType.debugIntegrationMessageAdded, (message) => {
            setMessages((p) => [message, ...p]);
            return Promise.resolve();
        });
        return () => {
            worker.actor.unregisterMessageHandler(MessageType.debugIntegrationMessageAdded);
        };
    }, []);

    const toggleEnabled = React.useCallback(() => {
        const next = !enabled;
        dispatch(setSetting('enableIntegrationMessageDebugger', next));
        worker.actor.sendAsync({
            type: MessageType.debugIntegrationMessagesEnableDisable,
            data: { enabled: next }
        });
        if (next) {
            setMessages([]);
        }
    }, [enabled]);

    return (
        <Window title={'Integration message debugger (FPM & desktop)'} onClose={props.onClose} draggable={true} initialHeight={720} initialWidth={820}>
            <div className="d-flex flex-column h-100 gap-3">
                <div className="d-flex gap-2 align-items-end justify-content-between">
                    <div className="secondary-text">
                        Shows messages exchanged with the FPM/desktop integration (same protocol for both). Enabling clears previously
                        captured messages.
                    </div>

                    <Button onClick={toggleEnabled} themeColor={enabled ? 'error' : 'success'}>
                        {enabled ? 'Stop' : 'Start'} listening
                    </Button>
                </div>

                <div className="d-flex flex-column gap-2 flex-grow-1 overflow-hidden">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="fw-semibold">Integration messages</div>
                        <div className="text-muted">{messages.length} entries</div>
                    </div>

                    <div className="d-flex flex-column gap-2 flex-grow-1 overflow-auto">
                        {messages.map((entry) => (
                            <IntegrationMessageEntry key={entry.id} message={entry} />
                        ))}

                        {!messages.length && <div className="border rounded p-4 text-muted">No messages to display yet.</div>}
                    </div>
                </div>
            </div>
        </Window>
    );
}

function IntegrationMessageEntry(props: { message: IntegrationDebugEntry }) {
    const [expanded, setExpanded] = React.useState(false);
    const isOutgoing = props.message.direction === 'outgoing';
    const subType = FPMMessageType[props.message.subType] ?? props.message.subType;

    return (
        <div className="border rounded d-flex flex-column gap-2 font-mono">
            <div className="d-flex align-items-center gap-2 px-2 flex-wrap">
                <Button
                    svgIcon={expanded ? ChevronUpOutline : ChevronDownOutline}
                    className="button-small"
                    fillMode="flat"
                    onClick={() => setExpanded(!expanded)}
                />
                <span className={clsx('badge rounded-1 px-2', isOutgoing ? 'text-bg-primary' : 'text-bg-info')}>
                    {isOutgoing ? 'Outgoing' : 'Incoming'}
                </span>
                {subType && <span className="badge rounded-1 px-2 text-bg-warning">{subType}</span>}
                <span className="flex-grow-1 text-end">{props.message.time}</span>
            </div>
            {expanded && (
                <pre
                    className="m-0 px-2 pb-1 rounded"
                    style={{
                        fontSize: 12,
                        overflowX: 'auto'
                    }}>
                    {JSON.stringify(props.message.content, null, 2)}
                </pre>
            )}
        </div>
    );
}

